import React, { useState, Fragment, useEffect, useMemo } from "react";
import { TextField, Chip, makeStyles } from "@material-ui/core";

import { Model } from "@ofqual/portal-core/core/types";
import { SearchDialog } from "./SearchDialog";
import { useIsMounted } from "@ofqual/portal-core/core/hooks";
import { SearchFieldProps } from "../types";

const useStyles = makeStyles((theme) => ({
  valuesContainer: { minHeight: "22px", display: "flex", flexWrap: "wrap" },
  valueChip: {
    marginTop: "5px",
  },
  input: {
    border: "none",
    "&:focus": {
      outline: "none",
    },
    background: "transparent",
  },
  readOnlyInput: {},
}));

export const SearchFieldMultiple = React.forwardRef<unknown, SearchFieldProps>(
  (props, ref) => {
    const classes = useStyles();
    const {
      onChange,
      value: incomingValue,
      lookupTracker,
      ...coreProps
    } = props;
    const { label, service, disabled, datatest, name, readOnly } = coreProps;

    const value = useMemo(() => (incomingValue || []) as (string | number)[], [
      incomingValue,
    ]);

    const [values, setValues] = useState<
      { id: string | number; label: string }[]
    >([]);

    const [dialogOpenProps, setDialogOpenProps] = useState({
      isDialogOpen: false,
      initialSearchText: "",
    });

    const isMounted = useIsMounted();

    const knownValuesLength = values.length;
    useEffect(() => {
      (async () => {
        if (knownValuesLength) {
          // only need to populate once on load, other wise we will
          //  hit the api every time a value changes, and for all current values
          //  on every change
          return;
        }
        const getCalls = value.map((item) => service.get(String(item)));

        const results = await Promise.all(getCalls);
        const mappedResults = results
          .filter((result): result is Model => !!result)
          .map((result) => ({
            id: result.id as string | number,
            label: result.label as string,
          }));

        if (lookupTracker && name) {
          mappedResults.forEach(({ id, label }) =>
            lookupTracker.register({ name, id, label })
          );
        }

        isMounted.current && setValues(mappedResults);
      })();
    }, [isMounted, value, service, knownValuesLength]);

    // reset state values if value is empty and currently have values left in state
    if (value.length === 0 && values.length) {
      setValues([]);
    }

    const handleSelectOrClose = (val?: { id: string; label: string }) => {
      setDialogOpenProps({ isDialogOpen: false, initialSearchText: "" });
      if (val) {
        handleAdd(val);
      }
    };

    const handleAdd = (val: { id: string; label: string }) => {
      const nextValues = [...values, val];
      setValues(nextValues);
      if (name && lookupTracker) {
        lookupTracker.register({
          name,
          id: val.id,
          label: val.label,
        });
      }
      onChange &&
        // we mimic the natural onChange shape of the underlying material-ui control,
        //  so build a shape that includes target:... etc. Also `type` is required so that
        //  `react-hook-form` processes the `onChange` argument like a material-ui argument,
        //  rather than in a primitive "the whole value I am getting back is the actual value" style
        //  see https://github.com/react-hook-form/react-hook-form/blob/3b880620fe2f2833501f17f15ea006d2fc0869fd/src/logic/getInputValue.ts#L8
        onChange(({
          type: true,
          name,
          target: {
            value: nextValues.map((item) => item.id),
          },
        } as unknown) as React.ChangeEvent<HTMLInputElement>);
    };

    const handleRemove = (val: { id: string | number }) => {
      const nextValues = values.filter((item) => item.id !== val.id);
      setValues(nextValues);
      onChange &&
        onChange(({
          type: true,
          name,
          target: {
            value: nextValues.map((item) => item.id),
          },
        } as unknown) as React.ChangeEvent<HTMLInputElement>);
    };

    const textLabel =
      readOnly === undefined || readOnly === false
        ? props.label
        : `${props.label} (Read Only)`;

    return (
      <Fragment>
        <TextField
          {...coreProps}
          data-test={datatest}
          fullWidth
          variant="outlined"
          multiline
          label={textLabel}
          disabled={disabled}
          onClick={() =>
            !disabled &&
            !readOnly &&
            setDialogOpenProps({ isDialogOpen: true, initialSearchText: "" })
          }
          onKeyPress={(e) =>
            !disabled &&
            !readOnly &&
            setDialogOpenProps({
              isDialogOpen: true,
              // if an alphanumeric character has been typed in the parent field, kick off the search with it
              initialSearchText: e.key.match(/^[A-Za-z0-9]{1}$/) ? e.key : "",
            })
          }
          // https://github.com/react-hook-form/react-hook-form/issues/2192
          // need to make it raise the label to above the field if there is a value
          InputLabelProps={{
            shrink: !!value.length,
          }}
          InputProps={{
            inputComponent: () => (
              <div className={classes.valuesContainer}>
                {values.map((item) => (
                  <React.Fragment key={item.id}>
                    <Chip
                      color="primary"
                      label={item.label}
                      onDelete={() => !disabled && handleRemove(item)}
                      className={classes.valueChip}
                      disabled={disabled}
                      data-test={"chip-tag"}
                    />
                    &nbsp;
                  </React.Fragment>
                ))}
                <input className={classes.input} disabled={disabled} />
              </div>
            ),
            readOnly,
            className: readOnly ? classes.readOnlyInput : undefined,
          }}
        ></TextField>

        <SearchDialog
          label={label}
          service={service}
          dialogOpenProps={dialogOpenProps}
          onSelectOrClose={handleSelectOrClose}
          selectedItems={values}
          datatest={datatest}
        />
      </Fragment>
    );
  }
);
